// client/src/components/cursos/CurriculoBuilder.js
// Régua de conteúdo do curso — módulos com aulas dentro, reordenáveis por
// drag-and-drop (mesma lib já usada em SectionsManager.js,
// @hello-pangea/dnd). Salva o array inteiro de uma vez em
// PUT /cursos/:id/curriculo — o `ordem` de cada módulo/aula é recalculado
// no backend a partir da posição no array recebido, então aqui só precisa
// reordenar localmente.
import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Trash2, GripVertical, Save, ChevronDown, ChevronUp, Video, FileText, File } from 'lucide-react';
import { useMutateCursos } from '../../hooks/useCursos';

const TIPO_AULA_OPCOES = [
    { value: 'video', label: 'Vídeo', icon: Video },
    { value: 'texto', label: 'Texto', icon: FileText },
    { value: 'pdf', label: 'PDF', icon: File },
];

const novoModulo = () => ({
    _localId: `novo-modulo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    titulo: 'Novo Módulo',
    descricao: '',
    aulas: []
});

const novaAula = () => ({
    _localId: `nova-aula-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    titulo: 'Nova Aula',
    descricao: '',
    tipo: 'texto',
    conteudo: { videoUrl: '', textoHtml: '', arquivoUrl: '' },
    duracaoEmMinutos: 0
});

const CurriculoBuilder = ({ cursoId, modulosIniciais }) => {
    const { atualizarCurriculo } = useMutateCursos();
    const [modulos, setModulos] = useState([]);
    const [moduloExpandido, setModuloExpandido] = useState(null);

    useEffect(() => {
        const base = (modulosIniciais || []).map(m => ({ ...m, aulas: (m.aulas || []).map(a => ({ ...a })) }));
        setModulos(base);
        if (base.length > 0) setModuloExpandido(base[0]._id || base[0]._localId);
    }, [modulosIniciais]);

    const chaveModulo = (m) => m._id || m._localId;
    const chaveAula = (a) => a._id || a._localId;

    const adicionarModulo = () => {
        const m = novoModulo();
        setModulos(prev => [...prev, m]);
        setModuloExpandido(m._localId);
    };

    const removerModulo = (idx) => {
        if (!window.confirm('Remover este módulo e todas as suas aulas?')) return;
        setModulos(prev => prev.filter((_, i) => i !== idx));
    };

    const atualizarModulo = (idx, campo, valor) => {
        setModulos(prev => prev.map((m, i) => i === idx ? { ...m, [campo]: valor } : m));
    };

    const adicionarAula = (moduloIdx) => {
        setModulos(prev => prev.map((m, i) => i === moduloIdx ? { ...m, aulas: [...m.aulas, novaAula()] } : m));
    };

    const removerAula = (moduloIdx, aulaIdx) => {
        setModulos(prev => prev.map((m, i) => i === moduloIdx
            ? { ...m, aulas: m.aulas.filter((_, ai) => ai !== aulaIdx) }
            : m));
    };

    const atualizarAula = (moduloIdx, aulaIdx, campo, valor) => {
        setModulos(prev => prev.map((m, i) => {
            if (i !== moduloIdx) return m;
            return {
                ...m,
                aulas: m.aulas.map((a, ai) => ai === aulaIdx ? { ...a, [campo]: valor } : a)
            };
        }));
    };

    const atualizarConteudoAula = (moduloIdx, aulaIdx, campo, valor) => {
        setModulos(prev => prev.map((m, i) => {
            if (i !== moduloIdx) return m;
            return {
                ...m,
                aulas: m.aulas.map((a, ai) => ai === aulaIdx ? { ...a, conteudo: { ...a.conteudo, [campo]: valor } } : a)
            };
        }));
    };

    const onDragEnd = (result) => {
        const { source, destination, type } = result;
        if (!destination) return;

        if (type === 'MODULO') {
            setModulos(prev => {
                const next = Array.from(prev);
                const [moved] = next.splice(source.index, 1);
                next.splice(destination.index, 0, moved);
                return next;
            });
            return;
        }

        // type === 'AULA' — droppableId no formato "aulas-<moduloIndex>"
        const moduloIdx = Number(source.droppableId.replace('aulas-', ''));
        if (source.droppableId !== destination.droppableId) return; // não permite arrastar aula entre módulos nesta versão

        setModulos(prev => prev.map((m, i) => {
            if (i !== moduloIdx) return m;
            const aulas = Array.from(m.aulas);
            const [moved] = aulas.splice(source.index, 1);
            aulas.splice(destination.index, 0, moved);
            return { ...m, aulas };
        }));
    };

    const handleSalvar = () => {
        const semIds = (m) => ({
            _id: m._id,
            titulo: m.titulo,
            descricao: m.descricao,
            aulas: (m.aulas || []).map(a => ({
                _id: a._id,
                titulo: a.titulo,
                descricao: a.descricao,
                tipo: a.tipo,
                conteudo: a.conteudo,
                duracaoEmMinutos: a.duracaoEmMinutos
            }))
        });
        atualizarCurriculo.mutate({ cursoId, modulos: modulos.map(semIds) });
    };

    if (!cursoId) {
        return (
            <div className="panel-state-wrapper panel-info">
                <span>Salve o curso primeiro. Depois de salvar, você poderá montar a régua de conteúdo aqui.</span>
            </div>
        );
    }

    return (
        <div className="curriculo-builder">
            <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">
                    Monte os módulos e aulas na ordem que o aluno deve seguir — a régua é sequencial: uma aula só libera depois que a anterior é concluída.
                </p>
                <button type="button" onClick={adicionarModulo} className="action-button action-button-secondary" style={{ whiteSpace: 'nowrap' }}>
                    <Plus size={18} className="icon" /> Adicionar Módulo
                </button>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="modulos" type="MODULO">
                    {(providedModulos) => (
                        <div {...providedModulos.droppableProps} ref={providedModulos.innerRef} className="space-y-3">
                            {modulos.map((modulo, moduloIdx) => (
                                <Draggable key={chaveModulo(modulo)} draggableId={String(chaveModulo(modulo))} index={moduloIdx}>
                                    {(providedModulo) => (
                                        <div ref={providedModulo.innerRef} {...providedModulo.draggableProps} className="border rounded-lg bg-white shadow-sm">
                                            <div className="flex items-center gap-2 p-3">
                                                <span {...providedModulo.dragHandleProps} className="cursor-grab text-gray-400">
                                                    <GripVertical size={18} />
                                                </span>
                                                <input
                                                    type="text"
                                                    value={modulo.titulo}
                                                    onChange={(e) => atualizarModulo(moduloIdx, 'titulo', e.target.value)}
                                                    className="form-input flex-1 font-medium"
                                                    placeholder="Título do módulo"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setModuloExpandido(prev => prev === chaveModulo(modulo) ? null : chaveModulo(modulo))}
                                                    className="action-button-icon text-gray-600"
                                                    title={moduloExpandido === chaveModulo(modulo) ? 'Recolher' : 'Expandir'}
                                                >
                                                    {moduloExpandido === chaveModulo(modulo) ? <ChevronUp size={18} className="icon" /> : <ChevronDown size={18} className="icon" />}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removerModulo(moduloIdx)}
                                                    className="action-button-icon text-red-600"
                                                    title="Remover módulo"
                                                >
                                                    <Trash2 size={18} className="icon" />
                                                </button>
                                            </div>

                                            {moduloExpandido === chaveModulo(modulo) && (
                                                <div className="px-3 pb-3">
                                                    <textarea
                                                        value={modulo.descricao || ''}
                                                        onChange={(e) => atualizarModulo(moduloIdx, 'descricao', e.target.value)}
                                                        className="form-input mb-3"
                                                        rows={2}
                                                        placeholder="Descrição do módulo (opcional)"
                                                    />

                                                    <Droppable droppableId={`aulas-${moduloIdx}`} type="AULA">
                                                        {(providedAulas) => (
                                                            <div {...providedAulas.droppableProps} ref={providedAulas.innerRef} className="space-y-2">
                                                                {modulo.aulas.map((aula, aulaIdx) => {
                                                                    const TipoIcon = TIPO_AULA_OPCOES.find(t => t.value === aula.tipo)?.icon || FileText;
                                                                    return (
                                                                        <Draggable key={chaveAula(aula)} draggableId={String(chaveAula(aula))} index={aulaIdx}>
                                                                            {(providedAula) => (
                                                                                <div ref={providedAula.innerRef} {...providedAula.draggableProps} className="border rounded p-3 bg-gray-50">
                                                                                    <div className="flex items-center gap-2 mb-2">
                                                                                        <span {...providedAula.dragHandleProps} className="cursor-grab text-gray-400">
                                                                                            <GripVertical size={16} />
                                                                                        </span>
                                                                                        <TipoIcon size={16} className="text-gray-500 icon" />
                                                                                        <input
                                                                                            type="text"
                                                                                            value={aula.titulo}
                                                                                            onChange={(e) => atualizarAula(moduloIdx, aulaIdx, 'titulo', e.target.value)}
                                                                                            className="form-input flex-1"
                                                                                            placeholder="Título da aula"
                                                                                        />
                                                                                        <select
                                                                                            value={aula.tipo}
                                                                                            onChange={(e) => atualizarAula(moduloIdx, aulaIdx, 'tipo', e.target.value)}
                                                                                            className="form-input"
                                                                                            style={{ width: '110px' }}
                                                                                        >
                                                                                            {TIPO_AULA_OPCOES.map(t => (
                                                                                                <option key={t.value} value={t.value}>{t.label}</option>
                                                                                            ))}
                                                                                        </select>
                                                                                        <input
                                                                                            type="number"
                                                                                            min={0}
                                                                                            value={aula.duracaoEmMinutos}
                                                                                            onChange={(e) => atualizarAula(moduloIdx, aulaIdx, 'duracaoEmMinutos', Number(e.target.value) || 0)}
                                                                                            className="form-input"
                                                                                            style={{ width: '90px' }}
                                                                                            title="Duração em minutos"
                                                                                        />
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => removerAula(moduloIdx, aulaIdx)}
                                                                                            className="action-button-icon text-red-600"
                                                                                            title="Remover aula"
                                                                                        >
                                                                                            <Trash2 size={16} className="icon" />
                                                                                        </button>
                                                                                    </div>

                                                                                    {aula.tipo === 'video' && (
                                                                                        <input
                                                                                            type="text"
                                                                                            value={aula.conteudo?.videoUrl || ''}
                                                                                            onChange={(e) => atualizarConteudoAula(moduloIdx, aulaIdx, 'videoUrl', e.target.value)}
                                                                                            className="form-input"
                                                                                            placeholder="URL do vídeo (YouTube, Vimeo ou S3)"
                                                                                        />
                                                                                    )}
                                                                                    {aula.tipo === 'texto' && (
                                                                                        <textarea
                                                                                            value={aula.conteudo?.textoHtml || ''}
                                                                                            onChange={(e) => atualizarConteudoAula(moduloIdx, aulaIdx, 'textoHtml', e.target.value)}
                                                                                            className="form-input"
                                                                                            rows={3}
                                                                                            placeholder="Conteúdo em texto (aceita HTML simples)"
                                                                                        />
                                                                                    )}
                                                                                    {aula.tipo === 'pdf' && (
                                                                                        <input
                                                                                            type="text"
                                                                                            value={aula.conteudo?.arquivoUrl || ''}
                                                                                            onChange={(e) => atualizarConteudoAula(moduloIdx, aulaIdx, 'arquivoUrl', e.target.value)}
                                                                                            className="form-input"
                                                                                            placeholder="URL do arquivo PDF"
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </Draggable>
                                                                    );
                                                                })}
                                                                {providedAulas.placeholder}
                                                            </div>
                                                        )}
                                                    </Droppable>

                                                    <button
                                                        type="button"
                                                        onClick={() => adicionarAula(moduloIdx)}
                                                        className="action-button action-button-secondary mt-2"
                                                    >
                                                        <Plus size={16} className="icon" /> Adicionar Aula
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {providedModulos.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {modulos.length === 0 && (
                <div className="panel-state-wrapper panel-info mt-3">
                    <span>Nenhum módulo ainda. Clique em "Adicionar Módulo" para começar a montar a régua de conteúdo.</span>
                </div>
            )}

            <div className="form-actions mt-4">
                <button
                    type="button"
                    onClick={handleSalvar}
                    disabled={atualizarCurriculo.isPending}
                    className="action-button action-button-primary"
                >
                    <Save size={18} className="icon" />
                    {atualizarCurriculo.isPending ? 'Salvando...' : 'Salvar Régua de Conteúdo'}
                </button>
            </div>
        </div>
    );
};

export default CurriculoBuilder;
